import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Checkbox,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
  Collapse,
  Button,
  Drawer,
  MenuItem,
} from '@mui/material';
import {
  Close,
  CalendarToday,
  Category,
  Search,
  ExpandMore,
  ExpandLess,
  FilterList,
  Home,
  Restaurant,
  AccessTime,
  Payment,
  Assessment,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, subDays, startOfWeek, startOfMonth, startOfYear } from 'date-fns';
import { glassStyle } from '../../theme/luxuryTheme';

interface FilterPanelProps {
  filters: any;
  onFiltersChange: (filters: any) => void;
  stores: any[];
  availableBrands: string[];
  availableSources: string[];
  onExecuteReport?: () => void;
}

const datePresets = [
  {
    label: 'Yesterday',
    getValue: () => ({
      startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'WTD',
    getValue: () => ({
      startDate: format(startOfWeek(new Date()), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'MTD',
    getValue: () => ({
      startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'YTD',
    getValue: () => ({
      startDate: format(startOfYear(new Date()), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Last 7 Days',
    getValue: () => ({
      startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Last 30 Days',
    getValue: () => ({
      startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Custom',
    getValue: () => null,
  },
];

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  stores,
  availableBrands,
  availableSources,
  onExecuteReport,
}) => {
  const [minimized, setMinimized] = useState(false);
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [selectedDatePreset, setSelectedDatePreset] = useState('Yesterday');

  const handleDatePresetChange = (preset: string) => {
    setSelectedDatePreset(preset);
    const presetConfig = datePresets.find((p) => p.label === preset);
    if (presetConfig) {
      const dates = presetConfig.getValue();
      if (dates) {
        onFiltersChange({
          ...filters,
          dateRange: {
            ...dates,
            preset,
          },
        });
      } else {
        onFiltersChange({
          ...filters,
          dateRange: {
            ...filters.dateRange,
            preset,
          },
        });
      }
    }
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: Date | null) => {
    if (value) {
      onFiltersChange({
        ...filters,
        dateRange: {
          ...filters.dateRange,
          [field]: format(value, 'yyyy-MM-dd'),
          preset: 'Custom',
        },
      });
    }
  };

  const handleSelectAllStores = (checked: boolean) => {
    if (checked) {
      onFiltersChange({
        ...filters,
        selectedStores: stores.map((s) => s.store_id || s.id),
      });
    } else {
      onFiltersChange({
        ...filters,
        selectedStores: [],
      });
    }
  };

  const filteredStores = stores.filter((store) =>
    (store.store_name || store.name || '').toLowerCase().includes(storeSearchTerm.toLowerCase())
  );

  return (
    <>
      <Paper sx={{ ...glassStyle, overflow: 'hidden' }}>
        {/* Header with Minimize Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            bgcolor: 'rgba(175, 35, 38, 0.05)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => setMinimized(!minimized)}
              sx={{
                color: 'primary.main',
                '&:hover': { bgcolor: 'rgba(175, 35, 38, 0.1)' },
              }}
            >
              {minimized ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
            <FilterList sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Report Generator
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {onExecuteReport && (
              <Button
                variant="contained"
                onClick={onExecuteReport}
                startIcon={<Assessment />}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: '#8b1a1d',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 20px rgba(175, 35, 38, 0.4)',
                  },
                  transition: 'all 0.2s ease',
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  fontSize: '1rem',
                }}
              >
                Generate Report
              </Button>
            )}
          </Box>
        </Box>

        {/* Filter Content */}
        <Collapse in={!minimized}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Store Selection with Checkboxes - Avero Style */}
              <Grid item xs={12} md={6} lg={3}>
                <Box
                  sx={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(175, 35, 38, 0.05)',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Home sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Business
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
                    {/* Search Box */}
                    {stores.length > 5 && (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search stores..."
                        value={storeSearchTerm}
                        onChange={(e) => setStoreSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />
                    )}

                    {/* All Checkbox */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.selectedStores.length === stores.length}
                          indeterminate={
                            filters.selectedStores.length > 0 &&
                            filters.selectedStores.length < stores.length
                          }
                          onChange={(e) => handleSelectAllStores(e.target.checked)}
                          sx={{
                            color: 'primary.main',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600, color: 'white' }}>All Stores</Typography>}
                      sx={{ mb: 1 }}
                    />

                    {/* Store List */}
                    <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                      {filteredStores.length > 0 ? (
                        filteredStores.map((store) => {
                          const storeId = store.store_id || store.id;
                          const storeName = store.store_name || store.name;
                          return (
                            <FormControlLabel
                              key={storeId}
                              control={
                                <Checkbox
                                  checked={filters.selectedStores.includes(storeId)}
                                  onChange={(e) => {
                                    const newSelected = e.target.checked
                                      ? [...filters.selectedStores, storeId]
                                      : filters.selectedStores.filter((id: string) => id !== storeId);
                                    onFiltersChange({
                                      ...filters,
                                      selectedStores: newSelected,
                                    });
                                  }}
                                  size="small"
                                  sx={{
                                    color: 'rgba(255,255,255,0.5)',
                                    '&.Mui-checked': {
                                      color: 'primary.main',
                                    },
                                  }}
                                />
                              }
                              label={<Typography variant="body2" sx={{ ml: 0.5 }}>{storeName}</Typography>}
                              sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%' }}
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', p: 1 }}>
                          No stores found
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Date Filters - Avero Style */}
              <Grid item xs={12} md={6} lg={4}>
                <Box
                  sx={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(175, 35, 38, 0.05)',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Date
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, maxHeight: 500, overflowY: 'auto' }}>
                    {/* Date Mode Buttons */}
                    <ToggleButtonGroup
                      value={filters.comparisonMode}
                      exclusive
                      onChange={(_, value) => {
                        if (value) {
                          onFiltersChange({
                            ...filters,
                            comparisonMode: value,
                          });
                        }
                      }}
                      fullWidth
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        '& .MuiToggleButton-root': {
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: 'text.secondary',
                          textTransform: 'capitalize',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(175, 35, 38, 0.2)',
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                    >
                      <ToggleButton value="range">Range</ToggleButton>
                      <ToggleButton value="trend">Trend</ToggleButton>
                      <ToggleButton value="comparison">Comparison</ToggleButton>
                    </ToggleButtonGroup>

                    {/* RANGE MODE */}
                    {filters.comparisonMode === 'range' && (
                      <RadioGroup value={selectedDatePreset} onChange={(e) => handleDatePresetChange(e.target.value)}>
                        {datePresets.map((preset) => (
                          <Box key={preset.label} sx={{ mb: 1.5 }}>
                            <FormControlLabel
                              value={preset.label}
                              control={<Radio size="small" />}
                              label={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {preset.label}
                                </Typography>
                              }
                            />
                            {preset.label === selectedDatePreset && preset.label !== 'Custom' && (
                              <Box sx={{ ml: 4, mt: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <DatePicker
                                    value={new Date(filters.dateRange.startDate)}
                                    disabled
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>to</Typography>
                                  <DatePicker
                                    value={new Date(filters.dateRange.endDate)}
                                    disabled
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                            {preset.label === 'Custom' && selectedDatePreset === 'Custom' && (
                              <Box sx={{ ml: 4, mt: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <DatePicker
                                    value={new Date(filters.dateRange.startDate)}
                                    onChange={(date) => handleCustomDateChange('startDate', date)}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>to</Typography>
                                  <DatePicker
                                    value={new Date(filters.dateRange.endDate)}
                                    onChange={(date) => handleCustomDateChange('endDate', date)}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </RadioGroup>
                    )}

                    {/* TREND MODE */}
                    {filters.comparisonMode === 'trend' && (
                      <Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                            Number of Periods
                          </Typography>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={filters.trendPeriods}
                            onChange={(e) => onFiltersChange({ ...filters, trendPeriods: Number(e.target.value) })}
                          >
                            {Array.from({ length: 52 }, (_, i) => i + 1).map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                            Period Type
                          </Typography>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={filters.trendPeriodType}
                            onChange={(e) => onFiltersChange({ ...filters, trendPeriodType: e.target.value })}
                          >
                            <MenuItem value="DAYS">DAYS</MenuItem>
                            <MenuItem value="SUN">SUN</MenuItem>
                            <MenuItem value="MON">MON</MenuItem>
                            <MenuItem value="TUE">TUE</MenuItem>
                            <MenuItem value="WED">WED</MenuItem>
                            <MenuItem value="THU">THU</MenuItem>
                            <MenuItem value="FRI">FRI</MenuItem>
                            <MenuItem value="SAT">SAT</MenuItem>
                            <MenuItem value="WEEKS">WEEKS</MenuItem>
                            <MenuItem value="WKND BLOCKS">WKND BLOCKS</MenuItem>
                            <MenuItem value="WKDY BLOCKS">WKDY BLOCKS</MenuItem>
                            <MenuItem value="MONTHS">MONTHS</MenuItem>
                            <MenuItem value="FPS">FPS</MenuItem>
                          </TextField>
                        </Box>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          ENDING
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                checked={filters.trendEndDate === format(subDays(new Date(), 1), 'yyyy-MM-dd')}
                                onChange={() => onFiltersChange({ ...filters, trendEndDate: format(subDays(new Date(), 1), 'yyyy-MM-dd') })}
                              />
                            }
                            label={<Typography variant="body2">Yesterday</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                checked={filters.trendEndDate !== format(subDays(new Date(), 1), 'yyyy-MM-dd')}
                                onChange={() => {}}
                              />
                            }
                            label=""
                          />
                          {filters.trendEndDate !== format(subDays(new Date(), 1), 'yyyy-MM-dd') && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                              <DatePicker
                                value={new Date(filters.trendEndDate)}
                                onChange={(date) => date && onFiltersChange({ ...filters, trendEndDate: format(date, 'yyyy-MM-dd') })}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* COMPARISON MODE */}
                    {filters.comparisonMode === 'comparison' && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                          vs Last Year
                        </Typography>

                        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={filters.comparisonTypes.daily}
                                onChange={(e) =>
                                  onFiltersChange({ ...filters, comparisonTypes: { ...filters.comparisonTypes, daily: e.target.checked } })
                                }
                              />
                            }
                            label="Daily"
                            sx={{ mb: 0.5 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={filters.comparisonTypes.wtd}
                                onChange={(e) =>
                                  onFiltersChange({ ...filters, comparisonTypes: { ...filters.comparisonTypes, wtd: e.target.checked } })
                                }
                              />
                            }
                            label="WTD"
                            sx={{ mb: 0.5 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={filters.comparisonTypes.mtd}
                                onChange={(e) =>
                                  onFiltersChange({ ...filters, comparisonTypes: { ...filters.comparisonTypes, mtd: e.target.checked } })
                                }
                              />
                            }
                            label="MTD"
                            sx={{ mb: 0.5 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={filters.comparisonTypes.fptd}
                                onChange={(e) =>
                                  onFiltersChange({ ...filters, comparisonTypes: { ...filters.comparisonTypes, fptd: e.target.checked } })
                                }
                              />
                            }
                            label="FPTD"
                            sx={{ mb: 0.5 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={filters.comparisonTypes.ytd}
                                onChange={(e) =>
                                  onFiltersChange({ ...filters, comparisonTypes: { ...filters.comparisonTypes, ytd: e.target.checked } })
                                }
                              />
                            }
                            label="YTD"
                          />
                        </Box>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          ENDING
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                checked={filters.comparisonEndDate === format(subDays(new Date(), 1), 'yyyy-MM-dd')}
                                onChange={() => onFiltersChange({ ...filters, comparisonEndDate: format(subDays(new Date(), 1), 'yyyy-MM-dd') })}
                              />
                            }
                            label={<Typography variant="body2">Yesterday</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                checked={filters.comparisonEndDate !== format(subDays(new Date(), 1), 'yyyy-MM-dd')}
                                onChange={() => {}}
                              />
                            }
                            label=""
                          />
                          {filters.comparisonEndDate !== format(subDays(new Date(), 1), 'yyyy-MM-dd') && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                              <DatePicker
                                value={new Date(filters.comparisonEndDate)}
                                onChange={(date) => date && onFiltersChange({ ...filters, comparisonEndDate: format(date, 'yyyy-MM-dd') })}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* More Filters Button */}
              <Grid item xs={12} md={6} lg={2}>
                <Box
                  sx={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(175, 35, 38, 0.05)',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FilterList sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      More Filters
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setMoreFiltersOpen(true)}
                      startIcon={<FilterList />}
                      sx={{
                        mb: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(175, 35, 38, 0.05)',
                        },
                      }}
                    >
                      Metrics
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setMoreFiltersOpen(true)}
                      startIcon={<Restaurant />}
                      sx={{
                        mb: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(175, 35, 38, 0.05)',
                        },
                      }}
                    >
                      Revenue Center
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setMoreFiltersOpen(true)}
                      startIcon={<AccessTime />}
                      sx={{
                        mb: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(175, 35, 38, 0.05)',
                        },
                      }}
                    >
                      Meal Period
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setMoreFiltersOpen(true)}
                      startIcon={<Category />}
                      sx={{
                        mb: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(175, 35, 38, 0.05)',
                        },
                      }}
                    >
                      Order Type
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setMoreFiltersOpen(true)}
                      startIcon={<Payment />}
                      sx={{
                        borderColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(175, 35, 38, 0.05)',
                        },
                      }}
                    >
                      Payment Method
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* More Filters Drawer */}
      <Drawer
        anchor="right"
        open={moreFiltersOpen}
        onClose={() => setMoreFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: 450,
            bgcolor: 'background.default',
            ...glassStyle,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              bgcolor: 'rgba(175, 35, 38, 0.05)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Advanced Filters
              </Typography>
              <IconButton onClick={() => setMoreFiltersOpen(false)} size="small">
                <Close />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              Select metrics and dimensions to analyze
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {/* Metrics Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                📊 Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('totalSales')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'totalSales']
                          : filters.selectedMetrics.filter((m: string) => m !== 'totalSales');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Total Sales"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('netSales')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'netSales']
                          : filters.selectedMetrics.filter((m: string) => m !== 'netSales');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Net Sales"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('orderCount')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'orderCount']
                          : filters.selectedMetrics.filter((m: string) => m !== 'orderCount');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Order Count"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('averageOrderValue')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'averageOrderValue']
                          : filters.selectedMetrics.filter((m: string) => m !== 'averageOrderValue');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Average Order Value"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('taxAmount')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'taxAmount']
                          : filters.selectedMetrics.filter((m: string) => m !== 'taxAmount');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Tax Amount"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('discountAmount')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'discountAmount']
                          : filters.selectedMetrics.filter((m: string) => m !== 'discountAmount');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Discount Amount"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('gratuity')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'gratuity']
                          : filters.selectedMetrics.filter((m: string) => m !== 'gratuity');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Gratuity"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMetrics.includes('serviceCharges')}
                      onChange={(e) => {
                        const newMetrics = e.target.checked
                          ? [...filters.selectedMetrics, 'serviceCharges']
                          : filters.selectedMetrics.filter((m: string) => m !== 'serviceCharges');
                        onFiltersChange({ ...filters, selectedMetrics: newMetrics });
                      }}
                    />
                  }
                  label="Service Charges"
                />
              </Box>
            </Box>

            {/* Revenue Centers */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                🍽️ Revenue Centers
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedRevenueCenters.includes('all')}
                      onChange={(e) => {
                        const newCenters = e.target.checked
                          ? ['all']
                          : [];
                        onFiltersChange({ ...filters, selectedRevenueCenters: newCenters });
                      }}
                    />
                  }
                  label="All Revenue Centers"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedRevenueCenters.includes('dineIn')}
                      onChange={(e) => {
                        const filtered = filters.selectedRevenueCenters.filter((c: string) => c !== 'all');
                        const newCenters = e.target.checked
                          ? [...filtered, 'dineIn']
                          : filtered.filter((c: string) => c !== 'dineIn');
                        onFiltersChange({ ...filters, selectedRevenueCenters: newCenters });
                      }}
                    />
                  }
                  label="Dine-In"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedRevenueCenters.includes('takeout')}
                      onChange={(e) => {
                        const filtered = filters.selectedRevenueCenters.filter((c: string) => c !== 'all');
                        const newCenters = e.target.checked
                          ? [...filtered, 'takeout']
                          : filtered.filter((c: string) => c !== 'takeout');
                        onFiltersChange({ ...filters, selectedRevenueCenters: newCenters });
                      }}
                    />
                  }
                  label="Takeout"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedRevenueCenters.includes('delivery')}
                      onChange={(e) => {
                        const filtered = filters.selectedRevenueCenters.filter((c: string) => c !== 'all');
                        const newCenters = e.target.checked
                          ? [...filtered, 'delivery']
                          : filtered.filter((c: string) => c !== 'delivery');
                        onFiltersChange({ ...filters, selectedRevenueCenters: newCenters });
                      }}
                    />
                  }
                  label="Delivery"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedRevenueCenters.includes('curbside')}
                      onChange={(e) => {
                        const filtered = filters.selectedRevenueCenters.filter((c: string) => c !== 'all');
                        const newCenters = e.target.checked
                          ? [...filtered, 'curbside']
                          : filtered.filter((c: string) => c !== 'curbside');
                        onFiltersChange({ ...filters, selectedRevenueCenters: newCenters });
                      }}
                    />
                  }
                  label="Curbside"
                />
              </Box>
            </Box>

            {/* Meal Periods */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                ⏰ Meal Periods
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMealPeriods.includes('allDay')}
                      onChange={(e) => {
                        const newPeriods = e.target.checked
                          ? ['allDay']
                          : [];
                        onFiltersChange({ ...filters, selectedMealPeriods: newPeriods });
                      }}
                    />
                  }
                  label="All Day"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMealPeriods.includes('breakfast')}
                      onChange={(e) => {
                        const filtered = filters.selectedMealPeriods.filter((p: string) => p !== 'allDay');
                        const newPeriods = e.target.checked
                          ? [...filtered, 'breakfast']
                          : filtered.filter((p: string) => p !== 'breakfast');
                        onFiltersChange({ ...filters, selectedMealPeriods: newPeriods });
                      }}
                    />
                  }
                  label="Breakfast (6am-11am)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMealPeriods.includes('lunch')}
                      onChange={(e) => {
                        const filtered = filters.selectedMealPeriods.filter((p: string) => p !== 'allDay');
                        const newPeriods = e.target.checked
                          ? [...filtered, 'lunch']
                          : filtered.filter((p: string) => p !== 'lunch');
                        onFiltersChange({ ...filters, selectedMealPeriods: newPeriods });
                      }}
                    />
                  }
                  label="Lunch (11am-3pm)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMealPeriods.includes('dinner')}
                      onChange={(e) => {
                        const filtered = filters.selectedMealPeriods.filter((p: string) => p !== 'allDay');
                        const newPeriods = e.target.checked
                          ? [...filtered, 'dinner']
                          : filtered.filter((p: string) => p !== 'dinner');
                        onFiltersChange({ ...filters, selectedMealPeriods: newPeriods });
                      }}
                    />
                  }
                  label="Dinner (3pm-10pm)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedMealPeriods.includes('lateNight')}
                      onChange={(e) => {
                        const filtered = filters.selectedMealPeriods.filter((p: string) => p !== 'allDay');
                        const newPeriods = e.target.checked
                          ? [...filtered, 'lateNight']
                          : filtered.filter((p: string) => p !== 'lateNight');
                        onFiltersChange({ ...filters, selectedMealPeriods: newPeriods });
                      }}
                    />
                  }
                  label="Late Night (10pm-2am)"
                />
              </Box>
            </Box>

            {/* Payment Methods */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                💳 Payment Methods
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedPaymentMethods.includes('all')}
                      onChange={(e) => {
                        const newMethods = e.target.checked
                          ? ['all']
                          : [];
                        onFiltersChange({ ...filters, selectedPaymentMethods: newMethods });
                      }}
                    />
                  }
                  label="All Payment Types"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedPaymentMethods.includes('card')}
                      onChange={(e) => {
                        const filtered = filters.selectedPaymentMethods.filter((p: string) => p !== 'all');
                        const newMethods = e.target.checked
                          ? [...filtered, 'card']
                          : filtered.filter((p: string) => p !== 'card');
                        onFiltersChange({ ...filters, selectedPaymentMethods: newMethods });
                      }}
                    />
                  }
                  label="Credit/Debit Card"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedPaymentMethods.includes('cash')}
                      onChange={(e) => {
                        const filtered = filters.selectedPaymentMethods.filter((p: string) => p !== 'all');
                        const newMethods = e.target.checked
                          ? [...filtered, 'cash']
                          : filtered.filter((p: string) => p !== 'cash');
                        onFiltersChange({ ...filters, selectedPaymentMethods: newMethods });
                      }}
                    />
                  }
                  label="Cash"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedPaymentMethods.includes('giftCard')}
                      onChange={(e) => {
                        const filtered = filters.selectedPaymentMethods.filter((p: string) => p !== 'all');
                        const newMethods = e.target.checked
                          ? [...filtered, 'giftCard']
                          : filtered.filter((p: string) => p !== 'giftCard');
                        onFiltersChange({ ...filters, selectedPaymentMethods: newMethods });
                      }}
                    />
                  }
                  label="Gift Cards"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedPaymentMethods.includes('mobile')}
                      onChange={(e) => {
                        const filtered = filters.selectedPaymentMethods.filter((p: string) => p !== 'all');
                        const newMethods = e.target.checked
                          ? [...filtered, 'mobile']
                          : filtered.filter((p: string) => p !== 'mobile');
                        onFiltersChange({ ...filters, selectedPaymentMethods: newMethods });
                      }}
                    />
                  }
                  label="Mobile Payment"
                />
              </Box>
            </Box>

            {/* Order Types */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                📦 Order Types
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedOrderTypes.includes('all')}
                      onChange={(e) => {
                        const newTypes = e.target.checked
                          ? ['all']
                          : [];
                        onFiltersChange({ ...filters, selectedOrderTypes: newTypes });
                      }}
                    />
                  }
                  label="All Orders"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedOrderTypes.includes('online')}
                      onChange={(e) => {
                        const filtered = filters.selectedOrderTypes.filter((t: string) => t !== 'all');
                        const newTypes = e.target.checked
                          ? [...filtered, 'online']
                          : filtered.filter((t: string) => t !== 'online');
                        onFiltersChange({ ...filters, selectedOrderTypes: newTypes });
                      }}
                    />
                  }
                  label="Online Orders"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedOrderTypes.includes('phone')}
                      onChange={(e) => {
                        const filtered = filters.selectedOrderTypes.filter((t: string) => t !== 'all');
                        const newTypes = e.target.checked
                          ? [...filtered, 'phone']
                          : filtered.filter((t: string) => t !== 'phone');
                        onFiltersChange({ ...filters, selectedOrderTypes: newTypes });
                      }}
                    />
                  }
                  label="Phone Orders"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedOrderTypes.includes('inPerson')}
                      onChange={(e) => {
                        const filtered = filters.selectedOrderTypes.filter((t: string) => t !== 'all');
                        const newTypes = e.target.checked
                          ? [...filtered, 'inPerson']
                          : filtered.filter((t: string) => t !== 'inPerson');
                        onFiltersChange({ ...filters, selectedOrderTypes: newTypes });
                      }}
                    />
                  }
                  label="In-Person Orders"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.selectedOrderTypes.includes('thirdParty')}
                      onChange={(e) => {
                        const filtered = filters.selectedOrderTypes.filter((t: string) => t !== 'all');
                        const newTypes = e.target.checked
                          ? [...filtered, 'thirdParty']
                          : filtered.filter((t: string) => t !== 'thirdParty');
                        onFiltersChange({ ...filters, selectedOrderTypes: newTypes });
                      }}
                    />
                  }
                  label="3rd Party (DoorDash, UberEats)"
                />
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              bgcolor: 'rgba(175, 35, 38, 0.05)',
              display: 'flex',
              gap: 2,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setMoreFiltersOpen(false)}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setMoreFiltersOpen(false)}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: '#8b1a1d' },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default FilterPanel;
